import {useTranslation} from '@aboutyou/i18n';
import React, {FC, useState} from 'react';
import styled from 'styled-components';
import {COLORS} from '../../../../../constants/colors';
import {FilterCard} from '../../../../atoms/FilterCard/FilterCard';
import {Text} from '../../../../atoms/Text/Text';
import {
  AttributeFilterItem,
  AttributeItems,
} from '../../FilterContent/Attribute/AttributeItems';
import {useFilteredAttributeItems} from '../../FilterContent/Attribute/useFilteredAttributeItems';
import {GenericFilterSearchField} from '../../FilterContent/Shared/GenericSearchField';
import {FilterFlyout} from '../FilterFlyout';
import {FilterFlyoutFooter} from '../FilterFlyoutFooter';
interface Props {
  activeItemIds: Array<string>;
  dataTestId?: string;
  hide: VoidFunction;
  submit: VoidFunction;
  isOpen: boolean;
  items: Array<AttributeFilterItem>;
  label: string;
  headline?: string;
  description?: string;
  onAttributeClick: (itemId: string, itemSlug?: string) => void;
  open: VoidFunction;
  isSingleSelect?: boolean;
  children?: React.ReactNode;
  renderInPortal?: boolean;
  shouldDisplaySearchField?: boolean;
  enableFlyoutFilter: boolean;
}

export const FilterFlyoutGeneric: FC<Props> = ({
  activeItemIds,
  children,
  dataTestId = 'filterFlyoutSectionAttribute',
  hide,
  submit,
  isOpen,
  items,
  label,
  headline,
  description,
  onAttributeClick,
  open,
  isSingleSelect,
  renderInPortal,
  shouldDisplaySearchField = false,
  enableFlyoutFilter,
}) => {
  const {t} = useTranslation();

  const [search, setSearch] = useState('');

  const handleClose = () => {
    setSearch('');
    hide();
  };

  const handleSubmit = () => {
    setSearch('');
    submit();
  };

  const filteredAttributeItems = useFilteredAttributeItems({
    search,
    shouldFilter: shouldDisplaySearchField,
    attributeItems: items,
  });
  return (
    <div data-testid={dataTestId}>
      <FilterCard
        count={activeItemIds.length ?? undefined}
        onClick={open}
        title={label}
        disableHorizontalPadding={enableFlyoutFilter}
      >
        {children}
      </FilterCard>
      <FilterFlyout
        data-testid={`filterFlyout_${dataTestId}`}
        onClose={handleClose}
        open={isOpen}
        title={label}
        onPrimaryButtonClick={handleSubmit}
        onSecondaryButtonClick={handleClose}
        primaryButtonLabel={t('Submit')}
        secondaryButtonLabel={t('Back')}
        footer={FilterFlyoutFooter}
        renderInPortal={renderInPortal}
      >
        <Wrapper $maxHeight={enableFlyoutFilter}>
          {shouldDisplaySearchField && (
            <StyledSearchField
              search={search}
              searchPlaceholder={`${t('Search for')} ${label}`}
              setSearch={setSearch}
            />
          )}
          {(headline || description) && (
            <HeadlineWrapper>
              <Text size="px12" color="Grey1">
                {description}
              </Text>
              <Text size="px14">{headline}</Text>
            </HeadlineWrapper>
          )}
          <AttributeItems
            dataTestId="filterFlyoutGeneric"
            items={filteredAttributeItems}
            activeItemIds={activeItemIds}
            onAttributeClick={onAttributeClick}
            isSingleSelect={isSingleSelect}
          />
        </Wrapper>
      </FilterFlyout>
    </div>
  );
};

const StyledSearchField = styled(GenericFilterSearchField)`
  padding: 1rem;
`;

const Wrapper = styled.div<{$maxHeight?: boolean}>`
  ${props => (props.$maxHeight ? 'height: 100%;' : '')}
`;

const HeadlineWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  background-color: ${COLORS.White};

  padding: 0 0 16px 0;
`;
